/**
 * SubmissionPrintView.jsx
 * Secretary-only, read-only on-screen preview of an online entry
 * submission — "in case things go wrong" — plus a "Download PDF" button
 * for a durable, pixel-perfect copy (same render pdf_utils.py produces for
 * the entrant's own confirmation email, via GET .../proof-pdf). Deliberately
 * reuses EntrySubmissionView (shared with EntryProofPage.jsx, the page that
 * PDF is actually rendered from) rather than window.print() on this modal —
 * @media print flattens the boxed on-screen look to a plain underlined
 * paper-form style, which doesn't match what the entrant actually saw and
 * signed. This component is otherwise standalone: no shared state or logic
 * with the live form, so it can never affect what an entrant sees or submits.
 *
 * Works for any submission status (received/pending/accepted/...), unlike
 * the review dialog — viewing/downloading is a read-only convenience that
 * stays useful after a decision's been made.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../EntryForm.css';
import { api, Spinner } from './SecretaryPortal';
import EntrySubmissionView from './EntrySubmissionView';

// This overlay is rendered inline inside the secretary page's own component
// tree (not a portal target elsewhere) — portal it out to a direct child of
// <body> (same pattern already used for other modals in SecretaryPortal.jsx)
// so it isn't clipped by any ancestor's own overflow/positioning.
const API_KEY = import.meta.env.VITE_API_KEY || '';

export default function SubmissionPrintView({ calendarId, submissionId, sessionToken, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    api(`/api/secretary/entries/${calendarId}/submissions/${submissionId}/print`, {}, sessionToken)
      .then(setData)
      .catch(err => setError(err.message));
  }, [calendarId, submissionId, sessionToken]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await fetch(
        `/api/secretary/entries/${calendarId}/submissions/${submissionId}/proof-pdf`,
        { headers: { 'X-API-Key': API_KEY, Authorization: `Bearer ${sessionToken}` } }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NHCA-entry-${submissionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return createPortal(
      <div className="sec-overlay">
        <div className="sec-modal" style={{ maxWidth: 480 }}>
          <div className="sec-modal-header">
            <h3>Couldn&apos;t load entry</h3>
            <button className="sec-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="sec-modal-body"><p>⚠️ {error}</p></div>
        </div>
      </div>,
      document.body
    );
  }

  if (!data) {
    return createPortal(
      <div className="sec-overlay">
        <div className="sec-modal" style={{ maxWidth: 480 }}><div className="sec-modal-body"><Spinner /></div></div>
      </div>,
      document.body
    );
  }

  const { event, submission } = data;

  return createPortal(
    <div className="sec-overlay submission-print-overlay" style={{ alignItems: 'flex-start', overflowY: 'auto', padding: '2rem 0' }}>
      <div className="secretary-portal App entry-form-page" style={{ background: 'white', maxWidth: 900, width: '95%', borderRadius: 8, padding: '1.5rem 2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          {downloadError && <span style={{ color: 'var(--danger-color)', fontSize: '0.85rem' }}>⚠️ {downloadError}</span>}
          <button className="btn-cancel" onClick={onClose}>Close</button>
          <button className="btn-save" onClick={handleDownloadPdf} disabled={downloading}>
            {downloading ? 'Generating…' : '⬇️ Download PDF'}
          </button>
        </div>

        <EntrySubmissionView event={event} submission={submission} />
      </div>
    </div>,
    document.body
  );
}
